import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Link,
  Divider
} from '@mui/material';
import { AccountTree, Upload, Home, ChevronLeft, ChevronRight, Security } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { APP_VERSION } from '../config/version';

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 64;

const MENU_ITEMS = [
  { text: 'Home', icon: <Home />, path: '/', description: 'About IntuneMermaid' },
  {
    text: 'Online Mode',
    icon: <AccountTree />,
    path: '/online',
    description: 'Create diagrams with live data',
    requiresAuth: true
  },
  {
    text: 'Offline Mode',
    icon: <Upload />,
    path: '/offline',
    description: 'Upload JSON for offline generation'
  },
  { text: 'Privacy', icon: <Security />, path: '/privacy', description: 'Privacy & data collection' }
];

const Sidebar = ({ isMobile, mobileOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Only the desktop drawer collapses.
  const collapsed = !isMobile && isCollapsed;
  const width = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile && onClose) onClose();
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowX: 'hidden' }}>
      {!isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'flex-end',
            px: 1,
            pt: 1,
            flexShrink: 0
          }}
        >
          <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
            <IconButton size="small" onClick={() => setIsCollapsed((prev) => !prev)}>
              {collapsed ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <List sx={{ px: collapsed ? 1 : 2, py: 1, flex: 1, overflowY: 'auto' }}>
        {MENU_ITEMS.map((item) => {
          const isActive = location.pathname === item.path;
          const isDisabled = item.requiresAuth && !isAuthenticated;

          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip
                title={collapsed ? item.text : ''}
                placement="right"
                disableHoverListener={!collapsed}
              >
                <ListItemButton
                  selected={isActive}
                  onClick={() => !isDisabled && handleNavigation(item.path)}
                  disabled={isDisabled}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': { backgroundColor: 'primary.dark' },
                      '& .MuiListItemIcon-root': { color: 'white' }
                    },
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&.Mui-disabled': { opacity: 0.6 }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 'auto' : 40,
                      color: isActive ? 'inherit' : 'action.active'
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {item.text}
                          {item.requiresAuth && !isAuthenticated && (
                            <Chip
                              label="Auth Required"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ fontSize: '0.6rem', height: 18 }}
                            />
                          )}
                        </Box>
                      }
                      secondary={item.description}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 600 : 400,
                        fontSize: '0.9rem'
                      }}
                      secondaryTypographyProps={{
                        fontSize: '0.75rem',
                        color: isActive ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      <Divider />

      {/* Footer - sponsor and version live here so the generator pages don't
          have to carry them below the fold. */}
      <Box
        sx={{
          flexShrink: 0,
          px: collapsed ? 1 : 2,
          py: 1.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Tooltip title={collapsed ? 'Sponsor this project' : ''} placement="right">
          <Link
            href="https://github.com/sponsors/schenardie"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              width: '100%',
              px: collapsed ? 0 : 2,
              py: 0.75,
              bgcolor: '#bf3989',
              color: '#ffffff',
              borderRadius: '6px',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              transition: 'background-color 0.2s',
              '&:hover': { bgcolor: '#9e2d72', textDecoration: 'none' }
            }}
          >
            <span role="img" aria-label="Sponsor">
              ❤️
            </span>
            {!collapsed && 'Sponsor'}
          </Link>
        </Tooltip>

        <Link
          href="/releases"
          onClick={(e) => {
            e.preventDefault();
            handleNavigation('/releases');
          }}
          sx={{
            color: 'text.secondary',
            textDecoration: 'none',
            fontSize: '0.75rem',
            '&:hover': { color: 'primary.main', textDecoration: 'underline' }
          }}
        >
          {collapsed ? `v${APP_VERSION}` : `Version ${APP_VERSION}`}
        </Link>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: 'background.paper'
          }
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: 'none', md: 'block' },
        width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          backgroundColor: 'background.paper',
          borderRight: (theme) => `1px solid ${theme.palette.divider}`,
          transition: 'width 0.25s ease',
          overflowX: 'hidden'
        }
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
